import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useThingEndorseMutation } from '../../../redux/thunks/things/thingendorse';
import { useThingUnEndorseMutation } from '../../../redux/thunks/things/thingunendorse';
import { useThingFollowMutation } from '../../../redux/thunks/things/thingfollow';
import { useThingUnFollowMutation } from '../../../redux/thunks/things/thingunfollow';
import { userApi } from '../../../redux/thunks/users/user';

/**
 * Custom hook for managing business interaction handlers
 * Encapsulates endorsement, following, and related state management
 */
export const useBusinessInteractions = ({ 
  token, 
  businessId, 
  title, 
  profileData,
  isTrustedThing,
  setIsTrustedThing,
  isFollowed,
  setIsFollowed
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Loading states
  const [endorseButtonLoading, setEndorseButtonLoading] = useState(false);
  const [unendorseButtonLoading, setUnendorseButtonLoading] = useState(false);
  const [followButtonLoading, setFollowButtonLoading] = useState(false);
  const [unfollowButtonLoading, setUnfollowButtonLoading] = useState(false);

  // Mutations
  const [triggerEndorse] = useThingEndorseMutation();
  const [triggerUnEndorse] = useThingUnEndorseMutation();
  const [triggerFollow] = useThingFollowMutation();
  const [triggerUnFollow] = useThingUnFollowMutation();

  // Helper function for authentication check
  const requireAuth = (callback) => {
    if (!token) {
      navigate('/auth/signin', { 
        state: { 
          name: 'businessDetails', 
          link: `/business/${businessId}/${title}` 
        } 
      });
      return false;
    }
    return callback();
  };

  // Endorse handler
  const handleEndorse = async () => {
    return requireAuth(async () => {
      setEndorseButtonLoading(true);
      
      const auth = {
        token: token,
        authType: 'token',
      };
      
      const payload = {
        auth,     
        businessId: profileData._id,
      };  
          
      try {
        await triggerEndorse({ payload });
        setIsTrustedThing(true);
        dispatch(userApi.util.invalidateTags(['Endorsements']));
      } catch (error) {
        console.error('Error endorsing business:', error);
      } finally {
        setEndorseButtonLoading(false);
      }
    });
  };

  // Unendorse handler
  const handleUnEndorsement = async () => {
    return requireAuth(async () => {
      setUnendorseButtonLoading(true);
      
      const auth = {
        token: token,
        authType: 'token',
      };
      
      const payload = {
        auth,     
        businessId: profileData._id,
      };  
      
      try {    
        await triggerUnEndorse({ payload });
        setIsTrustedThing(false);
        dispatch(userApi.util.invalidateTags(['Endorsements']));
      } catch (error) {
        console.error('Error unendorsing business:', error);
      } finally {
        setUnendorseButtonLoading(false);
      }
    });
  };

  // Follow handler
  const handleFollow = async () => {
    return requireAuth(async () => {
      setFollowButtonLoading(true);
      
      const auth = {
        token: token,
        authType: 'token',
      };
      let sp = {spId: profileData.spId, skillId: profileData.skillId};    
      const payload = {
        auth,
        sp,
        businessName: profileData.businessName,
        businessId: profileData._id
      };  
      
      try {  
        await triggerFollow({ payload });
        setIsFollowed(true);
        dispatch(userApi.util.invalidateTags(['Follow']));
      } catch (error) {
        console.error('Error following business:', error);
      } finally {
        setFollowButtonLoading(false);
      }
    });
  };

  // Unfollow handler
  const handleUnFollow = async () => {
    return requireAuth(async () => {
      setUnfollowButtonLoading(true);
      
      const auth = {
        token: token,
        authType: 'token',
      };
      let sp = {spId: profileData.spId, skillId: profileData.skillId};    
      const payload = {
        auth,
        sp,
        businessName: profileData.businessName,
        businessId: profileData._id
      };  
      
      try {  
        await triggerUnFollow({ payload });
        setIsFollowed(false);
        dispatch(userApi.util.invalidateTags(['Follow']));
      } catch (error) {
        console.error('Error unfollowing business:', error);
      } finally {
        setUnfollowButtonLoading(false);
      }
    });
  };

  // Return handlers and loading states
  return {
    handlers: {
      handleEndorse,
      handleUnEndorsement,
      handleFollow,
      handleUnFollow
    },
    loadingStates: {
      endorseButtonLoading,
      unendorseButtonLoading,
      followButtonLoading,
      unfollowButtonLoading
    }
  };
};